import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { z } from 'zod';
import {
  InviteSuperAdminUserRequest,
  PLATFORM_ROLES,
  type PlatformRole
} from '@bliks-io/contracts';
import { useInviteSuperAdminUser } from '@/pages/admin/api/mutations/useSuperAdminUsers';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn
} from '@bliks-io/ui';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { applyApiErrorToForm } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';

interface InviteSuperAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteSuperAdminDialog({ open, onOpenChange }: InviteSuperAdminDialogProps) {
  const { t } = useTranslation();
  const inviteUser = useInviteSuperAdminUser();

  const defaultValues: InviteSuperAdminUserRequest = {
    firstName: '',
    lastName: '',
    email: '',
    language: 'en',
    // Keine Vorgabe (ADR 0042): eine still vorbelegte Rolle wird überlesen, in
    // beide Richtungen. Wer jemanden zur Plattform holt, wählt sie bewusst.
    platformRoles: []
  };

  const onSubmit = async (
    data: InviteSuperAdminUserRequest,
    form: UseFormReturn<InviteSuperAdminUserRequest>
  ) => {
    if (data.platformRoles.length === 0) {
      form.setError('platformRoles', {
        message: t('adminSettings.superAdmin.roleRequired')
      });
      return;
    }
    try {
      await inviteUser.mutateAsync(data);
      onOpenChange(false);
    } catch (error: unknown) {
      // An already-registered email attaches inline to the email input; anything
      // else (schema failure, network, 5xx) collapses to a root message, which
      // FormDialog renders in its alert.
      applyApiErrorToForm(error as ApiError, form.setError, t, { fields: ['email'] });
    }
  };

  return (
    <FormDialog<InviteSuperAdminUserRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={t('adminSettings.superAdmin.inviteTitle')}
      description={t('adminSettings.superAdmin.inviteDescription')}
      icon={ShieldCheck}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitLabel={t('adminSettings.superAdmin.inviteSend')}
      size="xs"
    >
      {({ form }) => {
        const {
          register,
          watch,
          setValue,
          formState: { errors }
        } = form;
        // Der Prüfer liest Schlüssel aus dem Quelltext; ein Ausdruck im
        // Template wäre für ihn ein Schlüssel namens „platformRoles".
        const role = watch('platformRoles')?.[0];
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('common.FirstName')}</Label>
              <Input
                id="firstName"
                {...register('firstName', {
                  required: t('adminSettings.superAdmin.firstNameRequired')
                })}
                placeholder={t('organization.members.firstNamePlaceholder')}
                className={cn(errors.firstName && 'border-destructive')}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">{t('common.LastName')}</Label>
              <Input
                id="lastName"
                {...register('lastName', {
                  required: t('adminSettings.superAdmin.lastNameRequired')
                })}
                placeholder={t('organization.members.lastNamePlaceholder')}
                className={cn(errors.lastName && 'border-destructive')}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('common.Email')}</Label>
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: t('adminSettings.superAdmin.emailRequired'),
                  pattern: {
                    value: z.regexes.email,
                    message: t('adminSettings.superAdmin.emailInvalid')
                  }
                })}
                placeholder={t('organization.members.emailPlaceholder')}
                className={cn(errors.email && 'border-destructive')}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>

            {/* Was der oder die Eingeladene darf. Additiv gedacht, aber im
                Dialog eine Wahl: zwei Rollen zugleich ergeben heute nichts,
                was eine allein nicht schon gibt (ADR 0042). */}
            <div className="grid gap-2">
              <Label htmlFor="platformRole">{t('adminSettings.superAdmin.role')}</Label>
              <Select
                value={role ?? ''}
                onValueChange={(value) =>
                  setValue('platformRoles', [value as PlatformRole], {
                    shouldDirty: true,
                    shouldValidate: true
                  })
                }
              >
                <SelectTrigger id="platformRole" aria-invalid={Boolean(errors.platformRoles)}>
                  <SelectValue placeholder={t('adminSettings.superAdmin.rolePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {t(`adminSettings.superAdmin.roles.${role}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.platformRoles && (
                <p className="text-sm text-destructive">{errors.platformRoles.message}</p>
              )}
              {role && (
                <p className="text-muted-foreground text-xs">
                  {t(`adminSettings.superAdmin.roleHint.${role}`)}
                </p>
              )}
            </div>
          </div>
        );
      }}
    </FormDialog>
  );
}
